import React from 'react';
import { Unsubscribe } from 'redux';
import actions from '../../../store/actions';
import store from '../../../store/store';
import Modal from '../../Common/Modal/Modal';
import './MoreArgumentsModal.scss';
import conditionHelper from '../../../infrastructure/helpers/functions/condition/conditionHelper';
import '../CodeBlock.scss';
import { getOperandContent } from '../Condition/ContitionItem/ConditionItem.Rendering';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import Operand from '../../../types/functions/Operand/Operand';
import IngestionConfig from '../../../types/ingestion/IngestionConfig';
import SelectList from '../../Common/SelectList/SelectList';
import operandType from '../../../infrastructure/constants/functions/operandType';
import copyObject from '../../../infrastructure/helpers/common/copyObject';
import IndexedLookupArgumentsState from './IndexedLookupArgumentsState';
import Condition from '../../../types/functions/Condition/Condition';
import ComponentNumberAndAssignment from '../../../types/report/ComponentNumberAndAssignment';
import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import locationType from '../../../types/functions/Location/LocationType';
import ReportConfig from '../../../types/report/ReportConfig';
import copyArray from '../../../infrastructure/helpers/common/copyArray';
import DataSourceField from '../../../types/report/DataSourceField';
import Assignment from '../../../types/functions/Assignment/Assignment';
import ReportCaseField from '../../../types/report/ReportCaseField';
import AggregationFilter from '../../../types/report/AggregationFilter';
import { endpointFilterType } from '../../../infrastructure/constants/report/endpointFilterType';
import Aggregation from '../../../types/report/Aggregation';
import Selector from '../../../types/report/Selector';
import { findLookupOperandInAssignment, findLookupOperandInCondition, findLookupOperandInList } from './IndexedLookupArguments.Helpers';
import { findAggregatedRecordField } from '../../../infrastructure/helpers/functions/aggregation/aggregatedRecordFieldHelper';
import { createDataSourceOption, getDataType } from '../../Report/Lookup/LookupBuilder.Helpers';
import ReportCollections from '../../../infrastructure/types/Functions/ReportCollections';
import OperandModal from '../Operand/Modal/OperandModal';
import Dictionary from '../../../types/report/Dictionary';
import Lookup from '../../../types/functions/Lookup';
import ReportVariable from '../../../types/report/ReportVariable';
import OperandModalState from '../Operand/Modal/OperandModalState';
import operandHelper from '../../../infrastructure/helpers/functions/operand/operandHelper';
import { getEmptyOperandModalState } from '../Operand/Modal/OperandModal.Helpers';
import dataSourceHelper from '../../../infrastructure/helpers/functions/common/dataSourceHelper';
import OperandRules from '../../../types/functions/Operand/OperandRules';
import IngestionConfigOption from '../../../types/ingestion/IngestionConfigOption';
import { createDirectDataSourceOption } from '../Operand/Field/FieldValueBuilder.Helpers';

interface State {
    showArgumentsModal: boolean,
    label: string,
    elements: JSX.Element[],
    error: string | null,
    disabled: boolean,
    operands: Operand[],
    dataSource: IngestionConfig,
    location: ReportLogicLocation,
    operandIndex: number,
    argumentIndexes: number[],
    indexedLookupOperands: Operand[],
    clickedOperandTargetDataType: string,
    operandModalState: OperandModalState,
    selectedDataSourceFrom: string,
    selectedDataSourceTo: string,
    collections: ReportCollections,
}

interface Props {
    collections: ReportCollections,
    dictionaries: Dictionary[],
    lookups: Lookup[],
    variables: ReportVariable[],
    reportConfig: ReportConfig,
}

class IndexedLookupArgumentsModal extends React.Component<Props, State> {

    private unsubscribe: Unsubscribe | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            dataSource: {} as IngestionConfig,
            label: '',
            elements: [],
            showArgumentsModal: false,
            operands: [] as Operand[],
            error: null,
            disabled: false,
            location: {} as ReportLogicLocation,
            operandIndex: -1,
            argumentIndexes: [],
            indexedLookupOperands: [] as Operand[],
            clickedOperandTargetDataType: '',
            operandModalState: getEmptyOperandModalState(),
            selectedDataSourceFrom: '0',
            selectedDataSourceTo: '',
            collections: this.props.collections
        }

        this.onCloseArgumentsModal = this.onCloseArgumentsModal.bind(this);
        this.onSaveArgumentsModal = this.onSaveArgumentsModal.bind(this);
        this.onStateChange = this.onStateChange.bind(this);
        this.onOperandClick = this.onOperandClick.bind(this);
        this.onOperandModalSave = this.onOperandModalSave.bind(this);
        this.getOperands = this.getOperands.bind(this);
    }

    componentDidMount(): void {
        this.unsubscribe = store.subscribe(this.onStateChange);
    }

    componentWillUnmount(): void {
        (this.unsubscribe as Unsubscribe)();
    }

    onOperandClick(index: number, argumentIndexes: number[] = []) {

        let type = getDataType(this.state.indexedLookupOperands[index].fieldId as number, this.props.collections);

        let argumentsState = store.getState().indexedLookupArgumentsState;

        let root = copyObject(this.state.operands[index] || this.getEmptyOperand()) as Operand;

        let operand = argumentIndexes.length > 0 ?
            operandHelper.getOperand(root, { argumentIndexes } as ReportLogicLocation) :
            root;

        // Saved operands may miss default properties (e.g. `function`), which the operand builders expect.
        let defined = Object.fromEntries(Object.entries(operand).filter(([, value]) => value !== undefined && value !== null));
        operand = { ...this.getEmptyOperand(), ...defined } as Operand;

        if (!operand.operandType) {
            operand.operandType = operandType.field;
        }

        let modalState: OperandModalState = {
            ...getEmptyOperandModalState(),
            title: 'Operands',
            isOpen: true,
            operand,
            operandRules: {
                allowedTypes: [type]
            } as OperandRules,
            location: {
                statement: locationType.statement.indexedLookupOperands,
                argumentIndexes: [...argumentIndexes]
            } as ReportLogicLocation,
            dataSource1Id: argumentsState?.lookup?.dataSource2Id ?? null
        };

        this.setState({ operandIndex: index, argumentIndexes: [...argumentIndexes], clickedOperandTargetDataType: type, operandModalState: modalState }, () => {
            store.dispatch({ type: actions.indexedLookupOperandModal.open, payload: modalState });
        });
    }

    getEmptyOperand() {
        return {
            operandType: '',
            fieldId: null,
            lookupNumber: null,
            variableNumber: null,
            reportFieldId: null,
            customDataSourceFieldId: null,
            selectorNumber: null,
            dataType: '',
            value: '',
            function: '',
            arguments: [],
            dictionaryNumber: null,
            functionNumber: null,
            aggregationNumber: 0
        };
    }

    proceedWithAssignment(assignments: Assignment[],
        location: ReportLogicLocation,
        lookupNumber: number,
        operandNumber: number): Operand {

        return findLookupOperandInAssignment(assignments, location, operandNumber, lookupNumber) || {} as Operand;
    }

    proceedWithCondition(conditions: Condition[],
        location: ReportLogicLocation,
        lookupNumber: number,
        operandNumber: number): Operand {

        return findLookupOperandInCondition(conditions, location, operandNumber, lookupNumber) || {} as Operand;
    }

    getOperands(items: ComponentNumberAndAssignment[],
        location: ReportLogicLocation,
        lookupNumber: number,
        operandNumber: number): Operand {
        let item = items.find(item => item.number === location.statementNumber);

        // Keyed selectors (e.g. Anna Dsb Upi Enrichment, FIRDS) keep their logic in the assignment of each key.
        if (location.selectorKey) {

            let keyAssignment = ((item as Selector)?.keys || []).find(k => k.name === location.selectorKey)?.assignment;

            return keyAssignment ?
                this.proceedWithAssignment(assignmentHelper.asCollection(keyAssignment), location, lookupNumber, operandNumber) :
                {} as Operand;
        }

        if (item?.assignment) {

            let assignments = assignmentHelper.asCollection(item.assignment);

            return this.proceedWithAssignment(assignments, location, lookupNumber, operandNumber);
        }

        if (item?.condition && location.conditionNumber > -1) {
            let conditions = conditionHelper.asCollection(item?.condition as Condition);

            return this.proceedWithCondition(conditions, location, lookupNumber, operandNumber);
        }

        if (location?.statement === locationType.statement.aggregation && location.argumentIndexes?.length > 0) {
            let aggr = item as Aggregation;

            return findLookupOperandInList(aggr.groupBy, location, lookupNumber) || {} as Operand;
        }

        return {} as Operand;
    }

    prepareOperands(reportConfig: ReportConfig, location: ReportLogicLocation, lookupNumber: number, operandNumber: number): Operand {

        switch (location?.statement) {
            case locationType.statement.userDefinedFunction:
                return this.getOperands(reportConfig.userDefinedFunctions, location, lookupNumber, operandNumber);
            case locationType.statement.annaDsbUpiEnrichmentSelector:
                return this.getOperands(reportConfig.annaDsbUpiEnrichmentSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.annaDsbUpiSelector:
                return this.getOperands(reportConfig.annaDsbUpiSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.fcaRegulatedEntitiesSelector:
                return this.getOperands(reportConfig.fcaRegulatedEntitiesSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.firdsEsmaInstrumentSelector:
                return this.getOperands(reportConfig.firdsEsmaInstrumentSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.firdsFcaInstrumentSelector:
                return this.getOperands(reportConfig.firdsFcaInstrumentSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.lseSelector:
                return this.getOperands(reportConfig.lseSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.isinSelector:
                return this.getOperands(reportConfig.isinSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.leiSelector:
                return this.getOperands(reportConfig.leiSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.exchangeRatesSelector:
                return this.getOperands(reportConfig.exchangeRatesSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.symbolSelector:
                return this.getOperands(reportConfig.symbolSelectors, location, lookupNumber, operandNumber);
            case locationType.statement.filter:
                return this.getOperands(reportConfig.filters, location, lookupNumber, operandNumber);
            case locationType.statement.endpointFilter:
                return this.getOperands(reportConfig.endpointFilters.filter(x => x.type === endpointFilterType.regular), location, lookupNumber, operandNumber);
            case locationType.statement.overReportFilter:
                return this.getOperands(reportConfig.endpointFilters.filter(x => x.type === endpointFilterType.overReport), location, lookupNumber, operandNumber);
            case locationType.statement.underReportFilter:
                return this.getOperands(reportConfig.endpointFilters.filter(x => x.type === endpointFilterType.underReport), location, lookupNumber, operandNumber);
            case locationType.statement.lookup:
                return this.getOperands(reportConfig.lookups, location, lookupNumber, operandNumber);
            case locationType.statement.variable:
                return this.getOperands(reportConfig.variables, location, lookupNumber, operandNumber);
            case locationType.statement.validation:
                return this.getOperands(reportConfig.validations, location, lookupNumber, operandNumber);
            case locationType.statement.aggregation:
                return this.getOperands(reportConfig.aggregations || [], location, lookupNumber, operandNumber);
            case locationType.statement.aggregatedRecordField: {
                let field = findAggregatedRecordField(reportConfig, location) as DataSourceField;
                return this.proceedWithAssignment(assignmentHelper.asCollection(field.assignment),
                    location, lookupNumber, operandNumber);
            }
            case locationType.statement.aggregationFilter: {
                let field = reportConfig.aggregations.find(x => x.number === location.parentNumber)?.filters.find(x => x.number === location.statementNumber) as AggregationFilter;
                return this.proceedWithCondition(conditionHelper.asCollection(field.condition),
                    location, lookupNumber, operandNumber);
            }
            case locationType.statement.case:
                return this.getOperands(reportConfig.cases || [], location, lookupNumber, operandNumber);
            case locationType.statement.reportField: {
                let field = reportConfig.cases.find(x => x.number === location.statementNumber)?.fields.find(x => x.fieldId === location.fieldId) as ReportCaseField;
                return this.proceedWithAssignment(assignmentHelper.asCollection(field.assignment),
                    location, lookupNumber, operandNumber);
            }
            case locationType.statement.matchingKey:
                return this.proceedWithAssignment(assignmentHelper.asCollection(reportConfig.matchingKey.assignment) || [],
                    location, lookupNumber, operandNumber);
            case locationType.statement.matchingKeyEndPoint:
                return this.proceedWithAssignment(assignmentHelper.asCollection(reportConfig.matchingKeyEndPoint.assignment) || [],
                    location, lookupNumber, operandNumber);

            default: return {} as Operand;
        }
    }

    getOperand(index: number) {
        let originalOperand = this.state.operands[index];

        let operand = copyObject(originalOperand);

        if (operand) {
            return operand;
        }

        return {} as Operand;
    }

    onStateChange() {

        let state = store.getState();

        if (state.action === actions.indexedLookupOperandModal.save) {
            this.onOperandModalSave(state.indexedLookupOperandState);

            return;
        }

        if (state.action === actions.indexedLookupArgumentsModal.close) {
            this.setState({ showArgumentsModal: false });

            return;
        }

        if (state.action === actions.indexedLookupArgumentsModal.open) {

            let condition = state.indexedLookupArgumentsState.lookup.condition;

            let conditions = conditionHelper.asCollection(condition);

            let reportConfig = copyObject(this.props.reportConfig);

            let operand = this.prepareOperands(reportConfig,
                state.indexedLookupArgumentsState.location as ReportLogicLocation,
                state.indexedLookupArgumentsState.lookup.number,
                state.indexedLookupArgumentsState.operandNumber) as Operand;

            // Exactly one argument per lookup key: extra arguments (e.g. after keys were removed from the lookup) are dropped.
            let operands = copyArray(operand.arguments || []).slice(0, conditions.length);

            while (operands.length < conditions.length) {
                operands.push(this.getEmptyOperand());
            }

            let elements: JSX.Element[] = [];

            let indexedLookupOperands: Operand[] = [];

            let addAnd = true;

            conditions.forEach((condition, index) => {

                if (index === conditions.length - 1) {
                    addAnd = false;
                }

                let element = this.createElement(operands[index], state.indexedLookupArgumentsState, condition.operand2, index, addAnd);
                elements.push(element);

                indexedLookupOperands.push(condition.operand2);
            });

            let dataSource = state.indexedLookupArgumentsState.dataSources
                .find(x => x.id === state.indexedLookupArgumentsState.lookup.dataSource2Id) as IngestionConfig;

            this.setState({ operands, collections: this.props.collections, selectedDataSourceTo: dataSource?.id?.toString() || '', indexedLookupOperands, dataSource, showArgumentsModal: true, elements });
        }
    }

    onCloseArgumentsModal() {
        this.setState({ showArgumentsModal: false });
    }

    validateOperands() {
        let error = null;

        for (let i = 0; i < this.state.operands.length; i++) {
            if (!this.state.operands[i] || !this.state.operands[i].operandType) {
                error = `Operand not set up.`;
                break;
            }
        }

        return error;
    }

    onSaveArgumentsModal() {
        const error = this.validateOperands();
        if (!error) {
            this.setState({ error: null, showArgumentsModal: false });

            let state = store.getState();

            state.indexedLookupArgumentsState.operands = copyArray(this.state.operands);

            store.dispatch({ type: actions.indexedLookupArgumentsModal.save, payload: state.indexedLookupArgumentsState });
        }
        else {
            this.setState({ error });
        }
    }

    onClearClick(index: number) {
        let operands = copyArray(this.state.operands);

        operands[index] = operandHelper.getEmpty();

        this.setState({ operands, elements: this.buildElements(operands) });
    }

    onDatSourceFromChange = (id: number) => {

        if (id !== parseInt(this.state.selectedDataSourceFrom)) {

            let operands = this.state.operands.map(() => this.getEmptyOperand() as Operand);

            let dataSource = this.props.collections.dataSources
                .find(x => x.id === id) as IngestionConfig;

            let collections = { ...this.props.collections };

            if (dataSource) {
                collections.dataSourceOptions = [dataSource as IngestionConfigOption];
                collections.dataSources = [dataSource];
            }

            this.setState({ collections, dataSource, selectedDataSourceFrom: id?.toString(), operands, elements: this.buildElements(operands) });
        }
    }

    buildElements(operands: Operand[]): JSX.Element[] {

        let argumentsState = store.getState().indexedLookupArgumentsState;

        let lastIndex = this.state.indexedLookupOperands.length - 1;

        return this.state.indexedLookupOperands.map((lookupOperand, index) =>
            this.createElement(operands[index], argumentsState, lookupOperand, index, index !== lastIndex));
    }

    renderDataSources(): JSX.Element {

        let options = (this.props.collections?.dataSources || []).map(createDataSourceOption);

        return <>
            <div className='display-flex justify-content-around margin-bottom-10px border-bottom bg-whitesmoke'>
                <div>
                    <label className='default-label'>From:</label>
                    <SelectList
                        className='inline-select'
                        options={options}
                        includeEmptyOption={true}
                        value={this.state.selectedDataSourceFrom}
                        onChange={(id) => this.onDatSourceFromChange(parseInt(id))} />
                </div>
                <div>
                    <label className='default-label'>To:</label>
                    <SelectList
                        disabled={true}
                        className='inline-select'
                        options={options}
                        value={this.state.selectedDataSourceTo}
                        onChange={() => { }} />
                </div>
            </div>
            <div className='margin-top-20px' />
        </>;
    }

    createElement(operand1: Operand,
        state: IndexedLookupArgumentsState,
        operand2: Operand,
        index: number,
        addAnd: boolean): JSX.Element {
        return <div className='code-block display-flex'>
            {getOperandContent(
                operand1,
                1,
                [],
                null,
                state.functions,
                state.globalFunctions,
                state.lookups,
                state.dictionaries,
                state.variables,
                state.dataSources,
                state.reportFields,
                state.customDataSourceFields,
                state.lookup.condition.operand2.selectorNumber,
                (_operandNumber, argumentIndexes) => this.onOperandClick(index, argumentIndexes),
                false,
                state.reportConfigType,
                state.location,
                state.cases,
                state.aggregations
            )}
            <span className='padding-left-right-10px'>=</span>
            {getOperandContent(
                operand2,
                2,
                [],
                null,
                state.functions,
                state.globalFunctions,
                state.lookups,
                state.dictionaries,
                state.variables,
                state.dataSources,
                state.reportFields,
                state.customDataSourceFields,
                state.lookup.condition.operand2.selectorNumber,
                () => { },
                true,
                state.reportConfigType,
                state.location,
                state.cases,
                state.aggregations
            )}

            {addAnd && <div className='helper-btn padding-left-right-10px'>AND</div>}
            {<div className='cursor-pointer helper-btn padding-left-right-10px' onClick={() => this.onClearClick(index)}>CLEAR</div>}
        </div>
    }

    onOperandModalSave(operandModalState: OperandModalState): void {

        let operands = copyArray(this.state.operands);

        let argumentIndexes = this.state.argumentIndexes;

        if (argumentIndexes.length > 0 && operands[this.state.operandIndex]) {
            // Replace only the clicked argument inside the function, keeping the function itself.
            let parent = operandHelper.getOperand(operands[this.state.operandIndex], { argumentIndexes: argumentIndexes.slice(0, -1) } as ReportLogicLocation);
            (parent.arguments as Operand[])[argumentIndexes[argumentIndexes.length - 1]] = operandModalState.operand;
        }
        else {
            operands[this.state.operandIndex] = operandModalState.operand;
        }

        let operand = operands[this.state.operandIndex];

        let elements = [...this.state.elements];

        let state = store.getState();

        elements[this.state.operandIndex] =
            this.createElement(operand,
                state.indexedLookupArgumentsState, this.state.indexedLookupOperands[this.state.operandIndex], this.state.operandIndex,
                this.state.operandIndex < this.state.indexedLookupOperands.length - 1);

        this.setDataSource(operandModalState.operand);

        operandModalState = {
            isOpen: false,
            title: 'Operands',
            location:state.indexedLookupArgumentsState.location,
            operand: {
                operandType: operandModalState.operand.operandType
            }
        } as OperandModalState;

        this.setState({ operands, elements }, () => {
            store.dispatch({
                type: actions.operandModal.change,
                payload: operandModalState
            });
        });
    }

    setDataSource(operand: Operand) {

        let dataSource = this.props.collections.dataSources.find(x => x.id === this.props.reportConfig.rawDataSourceId) as IngestionConfig;

        if (operand.operandType === operandType.field) {
            dataSource = dataSourceHelper.getByFieldId(this.props.collections.dataSources, operand.fieldId as number);
        }

        if (operand.operandType === operandType.dictionary || operand.operandType === operandType.dictionaryField) {
            dataSource = dataSourceHelper.getByFieldId(this.props.collections.dataSources, operand.fieldId as number);
        }

        this.onDatSourceFromChange(dataSource.id as number);
    }

    render() {

        if (!this.state.showArgumentsModal) {
            return <></>;
        }

        return (
            <div className='multiple-arguments'>
                <Modal containerClassName='z-index-0-important' state={{ title: 'Operands', isOpen: this.state.showArgumentsModal, error: this.state.error }} size='large' close={this.onCloseArgumentsModal}>
                    <div className='flex-column arguments'>
                        {this.renderDataSources()}
                        {this.state.elements.map((item, index) => {
                            return <React.Fragment key={index}>{item}</React.Fragment>;
                        })
                        }
                    </div>
                    {!this.state.disabled &&
                        <div className='mt-3 mb-3 buttons-wrapper'>
                            <button className='btn save-btn'
                                onClick={this.onSaveArgumentsModal}>Save</button>
                        </div>
                    }

                </Modal>
                <OperandModal
                    size='large'
                    title='Operands'
                    openState={this.state.operandModalState}
                    openAction={actions.indexedLookupOperandModal.open}
                    onSaveAction={actions.indexedLookupOperandModal.save}
                    regime=''
                    reportType=''
                    collections={this.state.collections}
                    userDefinedFunctions={[]}
                    dictionaries={this.props.dictionaries}
                    lookups={this.props.lookups}
                    variables={this.props.variables}
                    isinSelectors={[]}
                    leiSelectors={[]}
                    cases={[]}
                    aggregations={[]}
                    exchangeRatesSelectors={[]}
                />
            </div >
        )
    }
}

export default IndexedLookupArgumentsModal;